import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

export function RecipientSecurityGatewaysGuide() {
  return (
    <div>
      <SEO
        title="Why Recipient Security Gateways Cause DMARC Failures"
        description="Learn why email security tools like Check Point, Proofpoint, and Mimecast can cause DMARC failures for legitimate emails, and why you don't need to fix them."
        canonical="/guides/recipient-security-gateways"
        ogType="article"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-blue-50/50 to-transparent py-16 dark:from-blue-950/20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to Help Center
          </Link>
          <div className="mt-8 flex items-center gap-3">
            <span className="badge badge-info">No Action Needed</span>
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            Why Recipient Security Gateways Cause DMARC Failures
          </h1>
          <p className="mt-6 text-xl text-muted-foreground leading-relaxed">
            Some DMARC failures aren't your fault — they're caused by the email security tools your recipients use.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">

          {/* Key Takeaway */}
          <div className="mb-12 rounded-2xl border-2 border-blue-200 bg-blue-50 p-6 dark:border-blue-800 dark:bg-blue-950/30">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">Key Takeaway</h3>
                <p className="mt-1 text-blue-800 dark:text-blue-200">
                  If you see failures from services like Check Point, Proofpoint, or Mimecast in your DMARC reports,
                  <strong> there's nothing wrong with your email configuration</strong>. These failures are expected
                  and don't require any action on your part.
                </p>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              What Are Security Gateways?
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                Many organizations use email security services to scan incoming messages for threats
                like phishing, malware, and spam. These services act as a "gateway" — all email passes
                through them before reaching the recipient's inbox.
              </p>
              <p>
                Popular email security gateways include:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-foreground">Check Point Harmony Email</strong> (formerly Avanan)</li>
                <li><strong className="text-foreground">Proofpoint</strong></li>
                <li><strong className="text-foreground">Mimecast</strong></li>
                <li><strong className="text-foreground">Cisco Secure Email</strong> (IronPort)</li>
                <li><strong className="text-foreground">Barracuda Email Gateway</strong></li>
                <li><strong className="text-foreground">Symantec/Broadcom Email Security</strong></li>
              </ul>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 2 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Why Do They Break DMARC?
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                When these security gateways operate in "inline" mode, they intercept and relay email
                before it reaches the recipient. This changes the email's path in a way that breaks
                SPF and DKIM authentication.
              </p>
            </div>

            <div className="mt-8 space-y-6">
              {/* Normal flow */}
              <div className="rounded-xl border p-6">
                <h4 className="font-semibold text-green-700 dark:text-green-400">Normal Email Flow</h4>
                <div className="mt-4 flex items-center gap-4 text-sm">
                  <div className="rounded-lg bg-green-100 dark:bg-green-900/30 px-4 py-2">Your Server</div>
                  <svg className="h-5 w-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                  <div className="rounded-lg bg-green-100 dark:bg-green-900/30 px-4 py-2">Recipient's Server</div>
                  <span className="badge badge-success">DMARC Pass</span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  SPF and DKIM signatures remain intact.
                </p>
              </div>

              {/* With gateway */}
              <div className="rounded-xl border p-6">
                <h4 className="font-semibold text-amber-700 dark:text-amber-400">With Security Gateway</h4>
                <div className="mt-4 flex items-center gap-4 text-sm flex-wrap">
                  <div className="rounded-lg bg-green-100 dark:bg-green-900/30 px-4 py-2">Your Server</div>
                  <svg className="h-5 w-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                  <div className="rounded-lg bg-blue-100 dark:bg-blue-900/30 px-4 py-2 border-2 border-blue-300 dark:border-blue-700">Security Gateway</div>
                  <svg className="h-5 w-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                  <div className="rounded-lg bg-green-100 dark:bg-green-900/30 px-4 py-2">Recipient's Server</div>
                  <span className="badge badge-warning">DMARC Fail</span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  The relay breaks SPF (different sending IP) and may break DKIM (modified headers).
                </p>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 3 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Why It's Safe to Ignore
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                These failures don't mean anything is wrong. Here's why:
              </p>
              <ol className="list-decimal list-inside space-y-4 ml-4">
                <li>
                  <strong className="text-foreground">Your email was sent correctly</strong> — it passed authentication
                  when it left your server.
                </li>
                <li>
                  <strong className="text-foreground">The email was delivered</strong> — the security gateway scanned it
                  and forwarded it to the recipient.
                </li>
                <li>
                  <strong className="text-foreground">DMARC reported correctly</strong> — the failure appears in reports
                  because the gateway's IP wasn't in your SPF record (and shouldn't be).
                </li>
              </ol>
              <p className="mt-6">
                You should <strong className="text-foreground">not</strong> add these security gateways to your
                SPF record. They're not your services — they belong to your recipients.
              </p>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 4 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              How to Identify Security Gateway Failures
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                GetSent automatically identifies known security gateways and marks them with a
                "No Action Needed" badge. You can verify by checking the PTR (reverse DNS) record:
              </p>
              <div className="mt-6 rounded-lg bg-muted p-4">
                <h4 className="font-medium text-foreground">Common PTR Patterns</h4>
                <ul className="mt-3 space-y-2 font-mono text-sm">
                  <li><code className="px-2 py-1 bg-background rounded">*.cloud-sec-av.com</code> — Check Point</li>
                  <li><code className="px-2 py-1 bg-background rounded">*.pphosted.com</code> — Proofpoint</li>
                  <li><code className="px-2 py-1 bg-background rounded">*.mimecast.com</code> — Mimecast</li>
                  <li><code className="px-2 py-1 bg-background rounded">*.iphmx.com</code> — Cisco</li>
                  <li><code className="px-2 py-1 bg-background rounded">*.barracudanetworks.com</code> — Barracuda</li>
                  <li><code className="px-2 py-1 bg-background rounded">*.messagelabs.com</code> — Symantec</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Frequently Asked Questions
            </h2>
            <div className="mt-8 space-y-6">
              <div className="rounded-lg border p-6">
                <h4 className="font-semibold">Can someone spoof a security gateway to hide attacks?</h4>
                <p className="mt-2 text-muted-foreground">
                  PTR records are controlled by the IP owner (usually cloud providers like AWS or Google),
                  who require domain verification. An attacker can't claim to be "cloud-sec-av.com" without
                  controlling that domain. We only match on verified, exact domain patterns.
                </p>
              </div>
              <div className="rounded-lg border p-6">
                <h4 className="font-semibold">Should I dismiss these failures in my reports?</h4>
                <p className="mt-2 text-muted-foreground">
                  You can dismiss them if you don't want to see them. They won't affect your DMARC policy
                  effectiveness or email deliverability.
                </p>
              </div>
              <div className="rounded-lg border p-6">
                <h4 className="font-semibold">Why do I see so many failures from one gateway?</h4>
                <p className="mt-2 text-muted-foreground">
                  If many of your recipients use the same security provider (common with enterprise customers),
                  you'll see more failures from that gateway. This is normal.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 p-8 dark:from-indigo-950/30 dark:to-blue-950/30">
            <h3 className="text-xl font-semibold">Need Help?</h3>
            <p className="mt-2 text-muted-foreground">
              If you're seeing failures that don't match these patterns, they may require action.
              Check your DMARC reports for unrecognized senders.
            </p>
            <Link
              to="/"
              className="btn btn-primary mt-4"
            >
              View Your Reports
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
}
